import React, { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../../Context";
import operationData from "../../../../../Operation/data.json";
import { fetchOrders } from "../../../../utils/foodDeliveryApi";
import { MdAttachMoney, MdArrowBack, MdTrendingUp } from "react-icons/md";
import FoodDeliveryTabs from "./FoodDeliveryTabs";

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  const candidate = response?.data || response?.items || response?.result || response?.body;
  return Array.isArray(candidate) ? candidate : [];
};

const getOrderFinancials = (order) => {
  const status = (order.orderStatus || order.status || "").toLowerCase();
  const payStatus = (order.paymentStatus || order.payment_status || "").toLowerCase();
  const payMethod = (order.paymentMethod || order.payment_method || "").toLowerCase();
  const amt = Number(order.totalAmount || order.amount || 0) || 0;

  // Revenue is based on payment status
  const isPaid = payStatus === "paid";

  // Refund Logic:
  // 1. COD: Refunded if payStatus is 'refunded', OR orderStatus is 'rejected', 'cancelled', or 'returned'
  // 2. Online: Refunded ONLY if payStatus is explicitly 'refunded'
  let isRefunded = false;

  if (payMethod === "cod") {
    isRefunded =
      payStatus === "refunded" ||
      status === "rejected" ||
      status === "cancelled" ||
      status === "returned";
  } else {
    // Online
    isRefunded = payStatus === "refunded";
  }

  const netRevenue = isPaid && !isRefunded ? amt : 0;

  return { isPaid, isRefunded, amt, netRevenue };
};

const completedStatuses = ["completed", "delivered", "payment_done", "confirmed", "paid"];

const Revenue = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || "";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCharts, setShowCharts] = useState(true);
  const [chartType, setChartType] = useState("daily");

  const loadOrders = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    try {
      const res = await fetchOrders(activeInstitution);
      setOrders(normalizeListResponse(res));
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [activeInstitution]);

  const { completedOrders, metrics, paymentSplit } = useMemo(() => {
    // Filter orders with paid or refunded payment status
    const completed = (orders || []).filter((order) => {
      const { isPaid, isRefunded } = getOrderFinancials(order);
      return isPaid || isRefunded;
    });

    let totalRevenue = 0;
    let refundedAmount = 0;
    let refundedCount = 0;
    const refundedOrders = [];

    completed.forEach((order) => {
      const { isPaid, isRefunded, amt } = getOrderFinancials(order);
      if (isPaid || isRefunded) {
        totalRevenue += amt;
      }
      if (isRefunded) {
        refundedAmount += amt;
        refundedCount++;
        refundedOrders.push(order);
      }
    });

    const netRevenue = totalRevenue - refundedAmount;

    // Payment split
    const online = completed.filter((o) => String(o.paymentMethod || o.payment_method || "").toLowerCase() !== "cod").length;
    const cod = completed.filter((o) => String(o.paymentMethod || o.payment_method || "").toLowerCase() === "cod").length;

    return {
      completedOrders: completed,
      metrics: {
        netRevenue,
        refundedAmount,
        totalRevenue,
        completedCount: completed.length,
        refundedCount: refundedCount,
      },
      paymentSplit: { online, cod },
    };
  }, [orders]);

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-4">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Revenue & Refunds</h1>
            <p className="mt-1 text-sm text-gray-500">{activeInstitution}</p>
          </div>

          {/* Statistics Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Net Revenue Card - Green */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-700">Net Revenue</p>
                  <p className="mt-2 text-3xl font-bold text-emerald-900">₹{metrics.netRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="mt-1 text-xs text-emerald-600">{metrics.completedCount} orders</p>
                </div>
                <div className="rounded-lg bg-emerald-100 p-3">
                  <MdAttachMoney className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </div>

            {/* Refunds Card - Red */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700">Refunds</p>
                  <p className="mt-2 text-3xl font-bold text-red-900">-₹{metrics.refundedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="mt-1 text-xs text-red-600">{metrics.refundedCount} orders</p>
                </div>
                <div className="rounded-lg bg-red-100 p-3">
                  <MdArrowBack className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Charts Toggle */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <button
              onClick={() => setShowCharts(!showCharts)}
              className="text-sm font-semibold text-gray-700 hover:text-gray-900"
            >
              {showCharts ? "Hide Visuals" : "Show Visuals"}
            </button>
          </div>

          {/* Charts Section */}
          {showCharts && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              {/* Trend Chart Controls */}
              <div className="flex gap-2">
                <button
                  onClick={() => setChartType("daily")}
                  className={`px-3 py-1 rounded text-sm font-medium ${chartType === "daily" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  Daily Trend
                </button>
                <button
                  onClick={() => setChartType("monthly")}
                  className={`px-3 py-1 rounded text-sm font-medium ${chartType === "monthly" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  Monthly Trend
                </button>
              </div>

              {/* Trend Chart Placeholder */}
              <div className="h-64 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MdTrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{chartType === "daily" ? "Daily" : "Monthly"} Revenue Trend</p>
                  <p className="text-xs text-gray-400">(Chart visualization)</p>
                </div>
              </div>

              {/* Payment Method Split */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Payment Method Split</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-sm text-gray-700">Online</span>
                    </div>
                    <span className="font-semibold text-gray-900">{paymentSplit.online} orders</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-800"></div>
                      <span className="text-sm text-gray-700">COD</span>
                    </div>
                    <span className="font-semibold text-gray-900">{paymentSplit.cod} orders</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transactions List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Transactions</h2>
            {loading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : completedOrders.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
                No completed orders found
              </div>
            ) : (
              <div className="space-y-3">
                {completedOrders.slice(0, 15).map((order, idx) => {
                  const orderId = order.orderId || order.id || idx;
                  const orderDate = new Date(order.createdAt || order.date || "");
                  const { isRefunded } = getOrderFinancials(order);

                  return (
                    <div key={idx} className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-b-0">
                      <div>
                        <p className="font-semibold text-gray-900">Order #{String(orderId).slice(-6).padStart(6, '0')}</p>
                        <p className="text-xs text-gray-500">
                          {orderDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} • {orderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">{order.customerName || "Guest"}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${isRefunded ? "line-through text-red-600" : "text-gray-900"}`}>
                          ₹{Number(order.totalAmount || order.amount || 0).toFixed(2)}
                        </p>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${isRefunded ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                          {isRefunded ? "Refunded" : "Paid"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Revenue;
